import type { PersonFormErrors, PersonFormValues } from '@/components/PersonFormFields'
import { documentTypeOf, isCnpjShape, isCpfShape, normalizeDocument } from '@/lib/document'
import { formatDocument } from '@/lib/format'
import { isMaskedValue, withoutMaskedValues } from '@/lib/sensitive'
import type { CustomerType, MaritalStatus } from '@/lib/types'

export const SPOUSE_STATUSES: readonly MaritalStatus[] = ['married', 'stableUnion']

export function hasSpouse(status?: MaritalStatus | '' | null): boolean {
  return !!status && SPOUSE_STATUSES.includes(status)
}

export const MARITAL_STATUS_LABEL: Record<MaritalStatus, string> = {
  single: 'Solteiro(a)',
  married: 'Casado(a)',
  divorced: 'Divorciado(a)',
  widowed: 'Viúvo(a)',
  stableUnion: 'União estável',
  separated: 'Separado(a)'
}

export const maritalStatusLabel = (status?: string | null): string | null =>
  status && Object.prototype.hasOwnProperty.call(MARITAL_STATUS_LABEL, status) ? MARITAL_STATUS_LABEL[status as MaritalStatus] : null

export const EMPTY_PERSON_FORM: PersonFormValues = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  countryCode: '+55',
  observations: '',
  document: '',
  documentType: 'cpf',
  maritalStatus: '',
  rg: '',
  rgIssuer: '',
  birthDate: '',
  profession: '',
  nationality: '',
  spouseName: '',
  spouseDocument: ''
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const digitsOf = (value?: string | null): string => (value ?? '').replace(/\D/g, '')

export const isValidEmail = (value?: string | null): boolean => EMAIL_PATTERN.test((value ?? '').trim())

export function personDocumentError(document: string, documentType: 'cpf' | 'cnpj'): string | null {
  if (!normalizeDocument(document) || isMaskedValue(document)) return null
  if (documentType === 'cpf' && !isCpfShape(document)) return 'CPF inválido'
  if (documentType === 'cnpj' && !isCnpjShape(document)) return 'CNPJ inválido'
  return null
}

export function isValidPhone(phone?: string | null, countryCode?: string | null): boolean {
  const digits = digitsOf(phone)
  const dial = digitsOf(countryCode) || '55'
  if (dial === '55') return digits.length === 10 || digits.length === 11
  return digits.length >= 4
}

const FIELD_LIMITS: { field: keyof PersonFormValues; max: number; message: string }[] = [
  { field: 'surname', max: 20, message: 'O apelido pode ter no máximo 20 caracteres.' },
  { field: 'rg', max: 20, message: 'O RG pode ter no máximo 20 caracteres.' },
  { field: 'rgIssuer', max: 20, message: 'O órgão emissor pode ter no máximo 20 caracteres.' },
  { field: 'profession', max: 80, message: 'A profissão pode ter no máximo 80 caracteres.' },
  { field: 'nationality', max: 60, message: 'A nacionalidade pode ter no máximo 60 caracteres.' }
]

export function customerFormErrors(values: PersonFormValues, { requireFullName = false }: { requireFullName?: boolean } = {}): PersonFormErrors {
  const errors: PersonFormErrors = {}
  const name = values.name.trim()

  if (!name) errors.name = 'Informe o nome do cliente.'
  else if (name.length < 2) errors.name = 'O nome precisa ter no mínimo 2 caracteres.'
  else if (requireFullName && name.split(/\s+/).length < 2) errors.name = 'Informe o nome e o sobrenome do cliente.'

  if (values.email.trim() && !isValidEmail(values.email)) errors.email = 'Informe um e-mail válido.'

  const documentError = personDocumentError(values.document, values.documentType)
  if (documentError) errors.document = documentError

  if (digitsOf(values.phone) && !isValidPhone(values.phone, values.countryCode)) errors.phone = 'Informe um telefone válido.'

  for (const { field, max, message } of FIELD_LIMITS) {
    const value = values[field]
    if (typeof value === 'string' && value.trim().length > max) errors[field] = message
  }

  return errors
}

export const hasFormErrors = (errors: PersonFormErrors): boolean => Object.values(errors).some(Boolean)

const optionalText = (value?: string | null): string | null => value?.trim() || null

const isoDateOrNull = (value?: string | null): string | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export function customerPayload(values: PersonFormValues) {
  const documentLocked = isMaskedValue(values.document)
  const document = documentLocked ? null : normalizeDocument(values.document)
  const spouse = hasSpouse(values.maritalStatus)
  return withoutMaskedValues({
    name: values.name.trim(),
    surname: optionalText(values.surname),
    email: optionalText(values.email),
    observations: optionalText(values.observations),
    ...(documentLocked ? {} : { document: document || null, documentType: document ? values.documentType : null }),
    maritalStatus: values.maritalStatus || null,
    rg: optionalText(values.rg),
    rgIssuer: optionalText(values.rgIssuer),
    birthDate: isoDateOrNull(values.birthDate),
    profession: optionalText(values.profession),
    nationality: optionalText(values.nationality),
    spouseName: spouse ? optionalText(values.spouseName) : null,
    spouseDocument: spouse ? optionalText(values.spouseDocument) : null
  })
}

const dateInputValue = (value?: string | Date | null): string => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}

export function customerFormValues(customer: Partial<CustomerType>): PersonFormValues {
  const document = isMaskedValue(customer.document) ? null : normalizeDocument(customer.document)
  return {
    ...EMPTY_PERSON_FORM,
    name: customer.name ?? '',
    surname: customer.surname ?? '',
    email: customer.email ?? '',
    observations: customer.observations ?? '',
    document: isMaskedValue(customer.document) ? (customer.document ?? '') : document ? formatDocument(document) : '',
    documentType: customer.documentType ?? documentTypeOf(document) ?? 'cpf',
    maritalStatus: customer.maritalStatus ?? '',
    rg: customer.rg ?? '',
    rgIssuer: customer.rgIssuer ?? '',
    birthDate: dateInputValue(customer.birthDate),
    profession: customer.profession ?? '',
    nationality: customer.nationality ?? '',
    spouseName: customer.spouseName ?? '',
    spouseDocument: customer.spouseDocument ?? ''
  }
}
